import CompanyBranding from '@src/components/CompanyBranding';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@src/components/ui/accordion';
import { Button } from '@src/components/ui/button';


export function TermsOfServicePage({ onNavigate }) {
  return (
    <div className="min-h-screen bg-white">
      {/* Header Section */}
      <div className="py-16 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <CompanyBranding onlyLogo={true} />

          <div className="mb-8">
            <h1 style={{ color: '#1e638d' }} className="text-3xl font-bold">
              SecureOneLabs
            </h1>
          </div>

          {/* Page Title */}
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            Terms of Service
          </h1>

          {/* Last Updated */}
          <p className="text-sm text-gray-500 mb-6">
            Last updated: January 12, 2026
          </p>

          {/* Intro Paragraph */}
          <p style={{ fontSize: '16px', lineHeight: '20px' }} className="text-base leading-relaxed text-gray-700 max-w-2xl mx-auto">
            Welcome to
            {' '}
            <span style={{ color: '#1e638d', fontWeight: '500' }}>BOSSS</span>
            , a product of
            {' '}
            <span style={{ color: '#1e638d', fontWeight: '500' }}>SecureOneLabs</span>
            . By using
            {' '}
            <span style={{ color: '#1e638d', fontWeight: '500' }}>BOSSS</span>
            , you agree to these terms.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <Accordion collapsible type="single" className="space-y-4">

              {/* Section 1: Definitions */}
              <AccordionItem value="definitions" className="border border-gray-200 rounded-lg shadow-sm">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <h2 style={{ color: '#1e638d' }} className="text-lg font-bold text-left">
                    1. Definitions
                  </h2>
                </AccordionTrigger>

                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4" style={{ fontSize: '16px', lineHeight: '20px' }}>
                    <p className="text-gray-700">
                      The following terms have specific meanings throughout this Agreement:
                    </p>

                    <div className="space-y-3">
                      <div className="border-l-2 border-gray-200 pl-4">
                        <h4 className="font-medium text-gray-900">&quot;Agreement&quot;</h4>
                        <p className="text-gray-700 text-sm">This Terms of Service document and any referenced policies or attachments.</p>
                      </div>

                      <div className="border-l-2 border-gray-200 pl-4">
                        <h4 className="font-medium text-gray-900">&quot;Platform&quot; or &quot;Services&quot;</h4>

                        <p className="text-gray-700 text-sm">
                          The
                          <span style={{ color: '#1e638d', fontWeight: '500' }}>BOSSS</span>
                          {' '}
                          cybersecurity management platform and all related features, tools, and functionalities provided by
                          <span style={{ color: '#1e638d', fontWeight: '500' }}>SecureOneLabs</span>
                          .
                        </p>
                      </div>

                      <div className="border-l-2 border-gray-200 pl-4">
                        <h4 className="font-medium text-gray-900">&quot;Customer&quot; or &quot;You&quot;</h4>
                        <p className="text-gray-700 text-sm">The entity or individual that has agreed to these terms and uses the Platform.</p>
                      </div>

                      <div className="border-l-2 border-gray-200 pl-4">
                        <h4 className="font-medium text-gray-900">&quot;Customer Data&quot;</h4>
                        <p className="text-gray-700 text-sm">Any data, files, content, or information uploaded or submitted to the Platform by or on behalf of the Customer.</p>
                      </div>

                      <div className="border-l-2 border-gray-200 pl-4">
                        <h4 className="font-medium text-gray-900">&quot;Confidential Information&quot;</h4>
                        <p className="text-gray-700 text-sm">Any non-public information disclosed by either party that is designated as confidential or should reasonably be understood to be confidential.</p>
                      </div>

                      <div className="border-l-2 border-gray-200 pl-4">
                        <h4 className="font-medium text-gray-900">&quot;Subscription Period&quot;</h4>
                        <p className="text-gray-700 text-sm">The period during which Customer has active access to the Platform as specified in the applicable order or subscription.</p>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Section 2: License Grant & Use of BOSSS */}
              <AccordionItem value="license-grant" className="border border-gray-200 rounded-lg shadow-sm">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <h2 style={{ color: '#1e638d' }} className="text-lg font-bold text-left">
                    2. License Grant & Use of BOSSS
                  </h2>
                </AccordionTrigger>

                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4" style={{ fontSize: '16px', lineHeight: '20px' }}>
                    <p className="text-gray-700">
                      Subject to the terms of this Agreement and payment of applicable fees,
                      {' '}
                      <span style={{ color: '#1e638d', fontWeight: '500' }}>SecureOneLabs</span>
                      {' '}
                      grants you a limited, non-exclusive, non-transferable, and non-sublicensable license to access and use
                      {' '}
                      <span style={{ color: '#1e638d', fontWeight: '500' }}>BOSSS</span>
                      {' '}
                      during the Subscription Period for your internal business purposes.
                    </p>

                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900">Permitted Uses:</h4>

                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>
                          Access and use
                          <span style={{ color: '#1e638d', fontWeight: '500' }}>BOSSS</span>
                          {' '}
                          for legitimate business purposes
                        </li>

                        <li>Configure the platform according to your organization&apos;s needs</li>
                        <li>Generate reports and analytics from your data</li>
                        <li>Integrate with approved third-party applications</li>
                      </ul>
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900">Prohibited Uses:</h4>

                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Attempting to breach security or access unauthorized areas</li>
                        <li>Using the platform for illegal activities</li>
                        <li>Reverse engineering, decompiling, or copying platform functionality</li>
                        <li>Interfering with other users&apos; access to the platform</li>
                        <li>Sublicensing, reselling, or distributing access to the Platform</li>
                        <li>Using the Platform in violation of applicable export control laws</li>
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Section 3: Accounts & Access */}
              <AccordionItem value="accounts-access" className="border border-gray-200 rounded-lg shadow-sm">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <h2 style={{ color: '#1e638d' }} className="text-lg font-bold text-left">
                    3. Accounts & Access
                  </h2>
                </AccordionTrigger>

                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4" style={{ fontSize: '16px', lineHeight: '20px' }}>
                    <p className="text-gray-700">
                      To access
                      {' '}
                      <span style={{ color: '#1e638d', fontWeight: '500' }}>BOSSS</span>
                      ,
                      you must create an account and maintain accurate information.
                    </p>

                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900">Account Requirements:</h4>

                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Provide accurate and complete registration information</li>
                        <li>Maintain the security of your login credentials</li>
                        <li>Notify us immediately of any unauthorized access</li>
                        <li>Accept responsibility for all activities under your account</li>
                      </ul>
                    </div>

                    <p className="text-gray-700">
                      <span style={{ color: '#1e638d', fontWeight: '500' }}>SecureOneLabs</span>
                      {' '}
                      reserves the right to suspend or terminate accounts that violate these terms.
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Section 4: Subscriptions & Billing */}
              <AccordionItem value="subscriptions-billing" className="border border-gray-200 rounded-lg shadow-sm">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <h2 style={{ color: '#1e638d' }} className="text-lg font-bold text-left">
                    4. Subscriptions & Billing
                  </h2>
                </AccordionTrigger>

                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4" style={{ fontSize: '16px', lineHeight: '20px' }}>
                    <p className="text-gray-700">
                      <span style={{ color: '#1e638d', fontWeight: '500' }}>BOSSS</span>
                      {' '}
                      is offered through various subscription plans with different features and pricing.
                    </p>

                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900">Billing Terms:</h4>

                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Subscription fees are billed in advance on a recurring basis</li>
                        <li>All fees are non-refundable unless otherwise stated</li>
                        <li>Price changes will be communicated 30 days in advance</li>
                        <li>Failed payments may result in service suspension</li>
                      </ul>
                    </div>

                    <p className="text-gray-700">
                      You may cancel your subscription at any time, with service continuing until the end of your current billing period.
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Section 5: Representations & Warranties */}
              <AccordionItem value="representations-warranties" className="border border-gray-200 rounded-lg shadow-sm">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <h2 style={{ color: '#1e638d' }} className="text-lg font-bold text-left">
                    5. Representations & Warranties
                  </h2>
                </AccordionTrigger>

                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4" style={{ fontSize: '16px', lineHeight: '20px' }}>
                    <p className="text-gray-700">
                      Each party represents and warrants to the other party that:
                    </p>

                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900">Mutual Representations:</h4>

                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>It has the full power and authority to enter into this Agreement</li>
                        <li>It is duly organized and in good standing under applicable law</li>
                        <li>This Agreement constitutes a legal, valid, and binding obligation</li>
                        <li>It has all necessary licenses and permits for the conduct of its business</li>
                      </ul>
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900">Customer Representations:</h4>

                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>You have the right to provide any Customer Data to the Platform</li>
                        <li>Your use of the Platform will comply with all applicable laws</li>
                        <li>You will not use the Platform for any unlawful purpose</li>
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Section 6: Intellectual Property & Data Ownership */}
              <AccordionItem value="ip-data-ownership" className="border border-gray-200 rounded-lg shadow-sm">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <h2 style={{ color: '#1e638d' }} className="text-lg font-bold text-left">
                    6. Intellectual Property & Data Ownership
                  </h2>
                </AccordionTrigger>

                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4" style={{ fontSize: '16px', lineHeight: '20px' }}>
                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900">SecureOneLabs Intellectual Property:</h4>

                      <p className="text-gray-700">
                        <span style={{ color: '#1e638d', fontWeight: '500' }}>SecureOneLabs</span>
                        {' '}
                        and its licensors own all right, title, and interest in the
                        <span style={{ color: '#1e638d', fontWeight: '500' }}>BOSSS</span>
                        {' '}
                        platform, including all software, algorithms, user interfaces, designs, and documentation. Nothing in this Agreement transfers any intellectual property rights to Customer.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900">Your Data Rights:</h4>

                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>
                          {'You retain ownership of all Customer Data you input into '}
                          <span style={{ color: '#1e638d', fontWeight: '500' }}>BOSSS</span>
                        </li>

                        <li>We do not claim ownership over your business data</li>
                        <li>You can export your data at any time</li>
                        <li>Data deletion requests will be honored according to our retention policy</li>
                      </ul>
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900">Usage Data:</h4>

                      <p className="text-gray-700">
                        <span style={{ color: '#1e638d', fontWeight: '500' }}>SecureOneLabs</span>
                        {' '}
                        may collect and use aggregated, de-identified usage data to improve the Platform and for analytics purposes. Such data will not identify you or your organization.
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Section 7: Data Protection */}
              <AccordionItem value="data-protection" className="border border-gray-200 rounded-lg shadow-sm">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <h2 style={{ color: '#1e638d' }} className="text-lg font-bold text-left">
                    7. Data Protection
                  </h2>
                </AccordionTrigger>

                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4" style={{ fontSize: '16px', lineHeight: '20px' }}>
                    <p className="text-gray-700">
                      <span style={{ color: '#1e638d', fontWeight: '500' }}>Data Protection</span>
                      {' '}
                      is fundamental to our service. We implement industry-standard security measures to protect your information.
                    </p>

                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900">Our Security Commitments:</h4>

                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Encryption of data in transit and at rest</li>
                        <li>Regular security audits and penetration testing</li>
                        <li>Role-based access controls and authentication</li>
                        <li>Compliance with relevant data protection regulations</li>
                      </ul>
                    </div>

                    <p className="text-gray-700">
                      While we implement robust security measures, you are responsible for maintaining the confidentiality of your access credentials and promptly reporting any suspected security incidents.
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Section 8: Confidentiality */}
              <AccordionItem value="confidentiality" className="border border-gray-200 rounded-lg shadow-sm">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <h2 style={{ color: '#1e638d' }} className="text-lg font-bold text-left">
                    8. Confidentiality
                  </h2>
                </AccordionTrigger>

                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4" style={{ fontSize: '16px', lineHeight: '20px' }}>
                    <p className="text-gray-700">
                      Each party agrees to protect the other party&apos;s Confidential Information with at least the same degree of care it uses to protect its own confidential information.
                    </p>

                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900">Confidentiality Obligations:</h4>

                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Use Confidential Information solely for purposes of this Agreement</li>
                        <li>Not disclose Confidential Information to third parties without consent</li>
                        <li>Limit access to employees and contractors with a need to know</li>
                        <li>Return or destroy Confidential Information upon request or termination</li>
                      </ul>
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900">Exclusions:</h4>

                      <p className="text-gray-700">
                        Confidential Information does not include information that: (i) is or becomes publicly available through no fault of the receiving party; (ii) was already known to the receiving party; (iii) is received from a third
                        party without restriction; or (iv) is independently developed without use of Confidential Information.
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Section 9: Indemnification */}
              <AccordionItem value="indemnification" className="border border-gray-200 rounded-lg shadow-sm">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <h2 style={{ color: '#1e638d' }} className="text-lg font-bold text-left">
                    9. Indemnification
                  </h2>
                </AccordionTrigger>

                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4" style={{ fontSize: '16px', lineHeight: '20px' }}>
                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900">Customer Indemnification:</h4>

                      <p className="text-gray-700">
                        You agree to indemnify, defend, and hold harmless
                        {' '}
                        <span style={{ color: '#1e638d', fontWeight: '500' }}>SecureOneLabs</span>
                        {' '}
                        and its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from:
                      </p>

                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Your use of the Platform in violation of this Agreement</li>
                        <li>Your Customer Data or any content you provide</li>
                        <li>Your violation of any applicable law or third-party rights</li>
                        <li>Any unauthorized use of the Platform under your account</li>
                      </ul>
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900">Indemnification Procedures:</h4>

                      <p className="text-gray-700">
                        The indemnified party shall promptly notify the indemnifying party of any claim and provide reasonable cooperation in the defense of such claim.
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Section 10: Disclaimer of Warranties */}
              <AccordionItem value="disclaimer-warranties" className="border border-gray-200 rounded-lg shadow-sm">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <h2 style={{ color: '#1e638d' }} className="text-lg font-bold text-left">
                    10. Disclaimer of Warranties
                  </h2>
                </AccordionTrigger>

                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4" style={{ fontSize: '16px', lineHeight: '20px' }}>
                    <p className="text-gray-700 font-medium uppercase text-sm">
                      EXCEPT AS EXPRESSLY PROVIDED IN THIS AGREEMENT, THE PLATFORM IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.
                    </p>

                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900">Disclaimers:</h4>

                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>We do not warrant that the Platform will be uninterrupted or error-free</li>
                        <li>We do not warrant that all defects will be corrected</li>
                        <li>We disclaim all implied warranties, including merchantability and fitness for a particular purpose</li>
                        <li>We are not responsible for third-party integrations or their availability</li>
                      </ul>
                    </div>

                    <p className="text-gray-700">
                      You acknowledge that independent verification of any suggestions, recommendations, or output provided by the Platform is your responsibility.
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Section 11: Limitation of Liability */}
              <AccordionItem value="limitation-liability" className="border border-gray-200 rounded-lg shadow-sm">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <h2 style={{ color: '#1e638d' }} className="text-lg font-bold text-left">
                    11. Limitation of Liability
                  </h2>
                </AccordionTrigger>

                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4" style={{ fontSize: '16px', lineHeight: '20px' }}>
                    <p className="text-gray-700">
                      <span style={{ color: '#1e638d', fontWeight: '500' }}>Limitation of Liability</span>
                      {' '}
                      provisions define the scope of our responsibility for service-related issues.
                    </p>

                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900">Liability Limitations:</h4>

                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Our aggregate liability shall not exceed the total fees paid in the twelve (12) months preceding the claim</li>
                        <li>We are not liable for indirect, incidental, special, consequential, or punitive damages</li>
                        <li>We are not liable for lost profits, data loss, or business interruption</li>
                        <li>These limitations apply regardless of the theory of liability</li>
                      </ul>
                    </div>

                    <p className="text-gray-700 text-sm">
                      Some jurisdictions do not allow the exclusion of certain warranties or limitations of liability, so some of the above may not apply to you.
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Section 12: Term & Termination */}
              <AccordionItem value="termination" className="border border-gray-200 rounded-lg shadow-sm">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <h2 style={{ color: '#1e638d' }} className="text-lg font-bold text-left">
                    12. Term & Termination
                  </h2>
                </AccordionTrigger>

                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4" style={{ fontSize: '16px', lineHeight: '20px' }}>
                    <p className="text-gray-700">
                      This Agreement begins on the date you first accept these terms and continues until terminated.
                    </p>

                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900">Termination Conditions:</h4>

                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>You may cancel your subscription at any time</li>
                        <li>We may terminate accounts for material violations of these terms</li>
                        <li>Either party may terminate upon 30 days written notice of a material breach that remains uncured</li>
                        <li>Immediate termination for security breaches or illegal activities</li>
                      </ul>
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900">Effect of Termination:</h4>

                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>You will have 30 days to export your data after termination</li>
                        <li>We will delete your data from our systems after the export period</li>
                        <li>Provisions relating to IP, confidentiality, indemnification, liability, and general terms survive termination</li>
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Section 13: Governing Law */}
              <AccordionItem value="governing-law" className="border border-gray-200 rounded-lg shadow-sm">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <h2 style={{ color: '#1e638d' }} className="text-lg font-bold text-left">
                    13. Governing Law & Dispute Resolution
                  </h2>
                </AccordionTrigger>

                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4" style={{ fontSize: '16px', lineHeight: '20px' }}>
                    <p className="text-gray-700">
                      This Agreement shall be governed by the laws of the United States, without regard to conflict of law principles.
                    </p>

                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900">Dispute Resolution:</h4>

                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Disputes will be resolved in state or federal courts located in California</li>
                        <li>Each party consents to the exclusive jurisdiction of such courts</li>
                        <li>Mediation is preferred before litigation for dispute resolution</li>
                        <li>Each party waives any right to a jury trial</li>
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Section 14: General Provisions */}
              <AccordionItem value="general-provisions" className="border border-gray-200 rounded-lg shadow-sm">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <h2 style={{ color: '#1e638d' }} className="text-lg font-bold text-left">
                    14. General Provisions
                  </h2>
                </AccordionTrigger>

                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4" style={{ fontSize: '16px', lineHeight: '20px' }}>
                    <div className="space-y-3">
                      <div className="border-l-2 border-gray-200 pl-4">
                        <h4 className="font-medium text-gray-900">Force Majeure</h4>

                        <p className="text-gray-700 text-sm">
                          Neither party shall be liable for delays or failures in performance resulting from causes beyond reasonable control, including acts of God, natural disasters, war, terrorism, labor disputes, government actions,
                          pandemics, or failures of third-party services.
                        </p>
                      </div>

                      <div className="border-l-2 border-gray-200 pl-4">
                        <h4 className="font-medium text-gray-900">Severability</h4>
                        <p className="text-gray-700 text-sm">If any provision of this Agreement is found to be unenforceable, the remaining provisions shall continue in full force and effect.</p>
                      </div>

                      <div className="border-l-2 border-gray-200 pl-4">
                        <h4 className="font-medium text-gray-900">Entire Agreement</h4>
                        <p className="text-gray-700 text-sm">This Agreement constitutes the entire agreement between the parties regarding its subject matter and supersedes all prior agreements, understandings, and communications.</p>
                      </div>

                      <div className="border-l-2 border-gray-200 pl-4">
                        <h4 className="font-medium text-gray-900">Assignment</h4>

                        <p className="text-gray-700 text-sm">
                          You may not assign or transfer this Agreement without our prior written consent. We may assign this Agreement in connection with a merger, acquisition, or sale of all or substantially all of our assets.
                        </p>
                      </div>

                      <div className="border-l-2 border-gray-200 pl-4">
                        <h4 className="font-medium text-gray-900">Waiver</h4>

                        <p className="text-gray-700 text-sm">
                          No waiver of any term shall be deemed a further or continuing waiver of such term or any other term. Failure to enforce any right shall not constitute a waiver of future enforcement.
                        </p>
                      </div>

                      <div className="border-l-2 border-gray-200 pl-4">
                        <h4 className="font-medium text-gray-900">Notices</h4>
                        <p className="text-gray-700 text-sm">All notices under this Agreement shall be in writing and sent to the email address associated with your account or the address specified in your subscription order.</p>
                      </div>

                      <div className="border-l-2 border-gray-200 pl-4">
                        <h4 className="font-medium text-gray-900">No Third-Party Beneficiaries</h4>
                        <p className="text-gray-700 text-sm">This Agreement does not create any third-party beneficiary rights in any individual or entity that is not a party to this Agreement.</p>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Section 15: Updates to Terms */}
              <AccordionItem value="updates" className="border border-gray-200 rounded-lg shadow-sm">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <h2 style={{ color: '#1e638d' }} className="text-lg font-bold text-left">
                    15. Updates to Terms
                  </h2>
                </AccordionTrigger>

                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4" style={{ fontSize: '16px', lineHeight: '20px' }}>
                    <p className="text-gray-700">
                      <span style={{ color: '#1e638d', fontWeight: '500' }}>SecureOneLabs</span>
                      {' '}
                      may update these terms from time to time to reflect changes in our service or legal requirements.
                    </p>

                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900">Update Process:</h4>

                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Material changes will be communicated 30 days in advance</li>
                        <li>Updates will be posted on this page with revision date</li>
                        <li>Continued use constitutes acceptance of updated terms</li>
                        <li>You may terminate if you disagree with material changes</li>
                      </ul>
                    </div>

                    <p className="text-gray-700">
                      We recommend reviewing these terms periodically to stay informed of any updates.
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>

            </Accordion>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="border-t border-gray-200 py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            {'Ready to Experience '}
            <span style={{ color: '#1e638d' }}>BOSSS</span>
            ?
          </h3>

          <p className="text-gray-600 mb-8 max-w-2xl mx-auto" style={{ fontSize: '16px', lineHeight: '20px' }}>
            See how
            {' '}
            <span style={{ color: '#1e638d', fontWeight: '500' }}>BOSSS</span>
            {' '}
            can transform your cybersecurity operations with our unified platform approach.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              style={{ fontSize: '16px' }}
              className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-all duration-200 hover:scale-105 hover:shadow-lg"
              onClick={() => onNavigate('demo')}
            >
              Schedule a Demo
            </Button>

            <Button
              variant="outline"
              style={{ fontSize: '16px' }}
              className="px-8 py-3 border-2 border-blue-600 text-blue-600 hover:bg-blue-50 font-medium rounded-lg transition-all duration-200 hover:scale-105"
              onClick={() => onNavigate('home')}
            >
              Back to Home
            </Button>
          </div>

          <div className="mt-8 text-sm text-gray-500">
            Have questions about these terms?
            {' '}
            <button className="text-blue-600 hover:underline font-medium" onClick={() => onNavigate('contact')}>Contact our team</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TermsOfServicePage;
